import axios from "axios";
var BASE_URL = process.env.REACT_APP_PLAYIT_CREATORAPI;

interface LocalizationResult {
  translations: TranslationData[];
}

interface TranslationData {
  text: string;
  to: string;
}

/**
 * A translation request that did not come back with one usable string per text.
 *
 * This used to be swallowed: the request was caught, logged, and an empty array
 * returned, so callers could not tell failure from success. They then wrote
 * `values[index]` for every key, which is `undefined` past the end of an empty
 * array — so a failed request replaced existing translations with `undefined`
 * and marked them auto-translated. Callers must now handle this.
 */
export class TranslationFailedError extends Error {
  readonly from: string;
  readonly to: string;
  /** The underlying axios error, when the request itself failed. */
  readonly reason?: unknown;

  constructor(from: string, to: string, detail: string, reason?: unknown) {
    super(`Translation ${from} → ${to} failed: ${detail}`);
    this.name = "TranslationFailedError";
    this.from = from;
    this.to = to;
    this.reason = reason;
  }
}

export class TranslationService {
  static async TranslateTexts(
    token: string,
    texts: string[],
    from: string,
    to: string
  ): Promise<string[]> {
    if (texts.length === 0) return [];

    var path = "/translate";
    var url = BASE_URL + path;

    var body = {
      languageFrom: from,
      languageTo: to,
      texts: texts,
    };

    var config = {
      headers: {
        Authorization: "Bearer " + token,
      },
    };

    let result: string[];
    try {
      const response = await axios.post<string[]>(url, body, config);
      result = response.data;
    } catch (error) {
      throw new TranslationFailedError(from, to, "the request failed", error);
    }

    // A 200 carrying the wrong number of strings is a failure too: callers pair
    // results with keys by index, so a short array silently blanks the tail.
    if (!Array.isArray(result) || result.length !== texts.length) {
      throw new TranslationFailedError(
        from,
        to,
        `expected ${texts.length} translations, received ${
          Array.isArray(result) ? result.length : typeof result
        }`
      );
    }

    return result;
  }
}
